import { useState, useMemo, useEffect, useCallback, useContext } from 'react'
import _ from 'lodash'
import { isValidPhoneNumber } from 'react-phone-number-input'
import validator from 'validator'
import moment from 'moment-timezone'
import { CardElement } from '@stripe/react-stripe-js'
import {
  AuthContext,
  useClearUserInfo,
  useHealthieInfo,
  useI18n,
  useInsuranceCardEntry,
  usePersonalInfo,
  useUnavailableStatus,
  useStateList,
  useUserProfile,
} from 'contexts/AuthContext'
import { showNotification } from 'utils/basic'
import {
  HEALTHIE_CREATE_FORM_ANSWER_GROUP_STRING,
  HEALTHIE_GET_USER_STRING,
  HEALTHIE_UPDATE_CLIENT_STRING,
  useHealthieAPIs,
} from 'apis/HealthieApiClient'
import {
  getHealthieUserId,
  getLocalLanguage,
  getStateId,
  getToken,
  getUserId,
  setHealthieProviderId,
  setStateId,
  setUserEmail,
} from 'utils/authLocalStorage'
import { isProduction } from 'apis/formatUrl'
import {
  addStripeInfoPromise,
  addUserPromise,
  checkUserExistsPromise,
  createReferringPhysicianPromise,
  forwardHealthiePromise,
  getMetadataOptionsPromise,
  payStripePromise,
  updateUserPromise,
  updateUserTagPromise,
} from 'apis/FitOnCareAPI'
import { ScreenKey } from 'views/Care/util'
import { setBrazeProfile } from 'utils/brazeInit'
import { logAmplitudeEvent, setAmplitudeProfile } from 'utils/amplitudeInit'
import { useSkuCondition } from './useSkuCondiction'
import useUpdateQuickNotes from './useUpdateQuickNotes'
import { UnavailableStatus } from 'views/Care/Unavailable'
import { useUserProfileStatus } from './useUserProfileStatus'

export const showErrorMessage = (error, i18n) => {
  console.error('request error', error)
  let errorMessage = 'There is something wrong, please try again later.'
  if (error.message) {
    errorMessage = error.message
  } else if (!_.isEmpty(error.messages)) {
    errorMessage = error.messages[0].message
  } else if (error.errors && error.errors[0]) {
    errorMessage = error.errors[0].message
  } else if (error instanceof Array && !_.isEmpty(error) && error[0].message) {
    errorMessage = error[0].message
  } else if (error.msg) {
    errorMessage = error.msg
  }
  showNotification({
    type: 'error',
    title: i18n(errorMessage),
  })
}

export const useCarePersonInfo = ({
  utmSource,
  utmCampaign,
  utmContent,
  sku,
  theme,
  themeId,
  onLoginSuccess,
  supportForNewWelcome,
} = {}) => {
  const { i18n } = useI18n()
  const { personalInfo, setPersonalInfo } = usePersonalInfo()
  const [firstName, setFirstName] = useState(personalInfo.firstName)
  const [lastName, setLastName] = useState(personalInfo.lastName)
  const [state, setState] = useState(personalInfo.state || {})
  const [phone, setPhone] = useState(personalInfo.phone)
  const [isPhoneValid, setIsPhoneValid] = useState()
  const [isEmailValid, setIsEmailValid] = useState()
  const [email, setEmail] = useState(personalInfo.email)
  const [isPolicyChecked, setIsPolicyChecked] = useState(true)
  const [isTelehealthConsent, setIsTelehealthConsent] = useState(true)
  const [isLoading, setIsLoading] = useState(false)
  const { getUser, createFormAnswerGroup } = useHealthieAPIs()
  const { updateClient } = useUpdateClient()
  const { clearAll } = useClearUserInfo()
  const { getNewSkuCondition } = useSkuCondition()
  const { stateList: stateOptions } = useStateList()
  const authContext = useContext(AuthContext)

  const [healthieUser, setHealthieUser] = useState({})
  useHealthieInfo({ user: healthieUser, supportForNewWelcome })
  const { getUserProfile, userProfile } = useUserProfile()

  useEffect(() => {
    getUserProfile()
  }, [getUserProfile])

  useEffect(() => {
    const phoneNumber = phone?.replace(/\D/g, '')
    const isValid = isValidPhoneNumber('+1' + phoneNumber)
    setIsPhoneValid(isValid)
  }, [phone])

  useEffect(() => {
    setIsEmailValid(validator.isEmail(email ?? ''))
  }, [email])

  const isNextButtonEnable = useMemo(() => {
    const isValid =
      !_.isEmpty(firstName) &&
      !_.isEmpty(lastName) &&
      !_.isEmpty(state) &&
      !_.isEmpty(phone) &&
      isPhoneValid &&
      !_.isEmpty(email) &&
      isEmailValid &&
      isPolicyChecked &&
      isTelehealthConsent &&
      !isLoading
    return isValid
  }, [
    firstName,
    lastName,
    state,
    phone,
    email,
    isPhoneValid,
    isEmailValid,
    isPolicyChecked,
    isTelehealthConsent,
    isLoading,
  ])

  const createFormAnswerGroupFun = async (state) => {
    try {
      const healthieUserId = getHealthieUserId()
      Promise.all([
        getHealthieRequest({
          healthieRequest: createFormAnswerGroup,
          queryString: HEALTHIE_CREATE_FORM_ANSWER_GROUP_STRING,
          variables: {
            finished: true,
            custom_module_form_id: isProduction()
              ? state.code === 'CA'
                ? '1886558'
                : '1886611'
              : state.code === 'CA'
                ? '976421'
                : '976420',
            user_id: healthieUserId,
            form_answers: [
              {
                custom_module_id: isProduction()
                  ? state.code === 'CA'
                    ? '24210631'
                    : '24211146'
                  : state.code === 'CA'
                    ? '8459034'
                    : '8459032',
                user_id: healthieUserId,
                answer: `I have read and agree to FitOn Care's Telehealth Consent.`,
              },
            ],
          },
          operationName: 'createFormAnswerGroup',
        }),
        getHealthieRequest({
          healthieRequest: createFormAnswerGroup,
          queryString: HEALTHIE_CREATE_FORM_ANSWER_GROUP_STRING,
          variables: {
            finished: true,
            custom_module_form_id: isProduction() ? '1886556' : '976417',
            user_id: healthieUserId,
            form_answers: [
              {
                custom_module_id: isProduction() ? '24210598' : '8459030',
                user_id: healthieUserId,
                answer: `I have read and agree to FitOn Care's Privacy Policy & Terms of Use`,
              },
            ],
          },
          operationName: 'createFormAnswerGroup',
        }),
      ])
    } catch (error) {
      console.log(error)
    }
  }

  const getUserInfo = async (variables) => {
    let options = stateOptions
    const { error, data } = await getHealthieRequest({
      healthieRequest: getUser,
      queryString: HEALTHIE_GET_USER_STRING,
      variables,
      operationName: 'getUser',
    })
    checkNeedToRelogin({ data, operationName: 'getUser', clearAll })
    if (data && !_.isEmpty(data.user)) {
      const user = data.user
      const localStateId = getStateId()
      const _state = _.find(
        options,
        localStateId
          ? { id: +localStateId }
          : { legalEntityId: user.dietitian_id }
      )

      updateNewUserInfo({
        ...user,
        state: _state && _state.type !== 3 ? _state : state,
      })
      setHealthieUser(user)
    }
    return { error, data }
  }

  const updateNewUserInfo = async (user) => {
    if (user) {
      setPersonalInfo({
        firstName: user.legal_name ? user.legal_name : user.first_name,
        firstNameForMca: user.first_name,
        lastName: user.last_name,
        phone: user.phone_number,
        email: user.email,
        state: user.state,
      })
      setFirstName(user.legal_name ? user.legal_name : user.first_name)
      setLastName(user.last_name)
      setPhone(user.phone_number)
      setEmail(user.email)
      setState(user.state)
    }
  }

  const updateUserInfo = async (state) => {
    const updateUserBody = {
      firstName: firstName,
      lastName: lastName,
      phoneNumber: phone,
      stateId: state.id,
    }
    if (utmSource && utmCampaign && utmContent) {
      updateUserBody.utm = {
        source: utmSource,
        campaign: utmCampaign,
        content: utmContent,
      }
    }
    const user = await updateUserPromise(updateUserBody)
    setBrazeProfile({
      firstName: firstName,
      lastName: lastName,
      email: email,
      phone: phone,
      utmSource,
      utmCampaign,
      utmContent,
      sku,
    })
    setUserEmail(email)
    setAmplitudeProfile({ utmSource, utmCampaign, utmContent })
    const userStatus = user?.skuStatus?.[sku]
    await updateUserPromise(
      {
        ...(userStatus !== 2 && {
          skuStatus: {
            sku,
            status: 1,
          },
        }),
        skuCondition: getNewSkuCondition({
          skuCondition: user?.skuCondition,
          themeIds: themeId,
          sku,
        }),
      },
      authContext
    )
  }

  const updateUserHealthieInfo = async (userId) => {
    const _firstName = firstName
    const variables = {
      id: userId,
      first_name: _firstName,
      last_name: lastName,
      phone_number: phone,
    }
    updateClient({
      variables,
    })
  }

  const signUpNewUser = async () => {
    const signUpResp = await addUserPromise({
      firstName: firstName,
      lastName: lastName,
      email: email,
      countryCode: '1',
      phoneNumber: phone,
      stateId: state.id,
      utmSource,
      utmCampaign,
      utmContent,
    })
    if (signUpResp.code !== 200) {
      showErrorMessage(signUpResp, i18n)
    } else if (signUpResp.data) {
      const userInfo = signUpResp.data
      onLoginSuccess({ ...userInfo, stateType: state.type })
      await createFormAnswerGroupFun(state)
      if (userInfo?.id) {
        setBrazeProfile({
          id: userInfo.id,
          healthieID: getHealthieUserId(),
          firstName: firstName,
          lastName: lastName,
          email: email,
          phone: phone,
          state: state.name,
          legalEntity: state.type === 1 ? 'CA' : state.type === 2 ? 'NY' : 'FL',
          utmSource,
          utmCampaign,
          utmContent,
          sku,
        })
        setAmplitudeProfile({
          utmSource,
          utmCampaign,
          utmContent,
          state: state.name,
        })
      }
      const userStatus = userInfo?.skuStatus?.[sku]
      await updateUserPromise(
        {
          ...(userStatus !== 2 && {
            skuStatus: {
              sku,
              status: 1,
            },
          }),
          skuCondition: getNewSkuCondition({
            skuCondition: userInfo?.skuCondition,
            themeIds: themeId,
            sku,
          }),
          insuranceProvided: false,
        },
        authContext
      )
      return true
    }
    return false
  }

  const uploadPersonalInfo = async ({
    onSuccess = () => {},
    onVerifyEmail = () => {},
  } = {}) => {
    setPersonalInfo({
      firstName,
      lastName,
      phone,
      email,
      state,
    })
    setHealthieProviderId(state.legalEntityId)
    setIsLoading(true)
    try {
      const token = getToken()
      const userId = getHealthieUserId()
      let isSameEmail = userProfile?.email === email
      const isStateChanged =
        userProfile && userProfile.localeStateId !== state.id
      if (token && userId && !isStateChanged && isSameEmail) {
        updateUserHealthieInfo(userId)
        await updateUserInfo(state)
        updateNewUserInfo({ ...healthieUser, state })
        setStateId(state.id)
        await getUserProfile()
        onSuccess?.()
        return
      }

      const resp = await checkUserExistsPromise(email, state.id)
      const shouldVerifyEmail = resp.exists
      if (shouldVerifyEmail) {
        setUserEmail(email)
        logAmplitudeEvent('Redirect: Healthie - Existing User', {
          sku,
          theme,
          language: getLocalLanguage(),
        })
        onVerifyEmail?.()
        return
      }
      const success = await signUpNewUser()
      if (success) {
        const { data: newData } = await getHealthieRequest({
          healthieRequest: getUser,
          queryString: HEALTHIE_GET_USER_STRING,
          variables: { id: getHealthieUserId(), include_tags: true },
          operationName: 'getUser',
        })
        setHealthieUser(newData?.user)
        updateNewUserInfo({ ...newData?.user, state })
        onSuccess?.()
      }
      setIsLoading(false)
    } catch (error) {
      setIsLoading(false)
      showErrorMessage(error, i18n)
    }
  }

  return {
    firstName,
    setFirstName,
    lastName,
    setLastName,
    state,
    setState,
    phone,
    setPhone,
    isPhoneValid,
    setIsPhoneValid,
    isEmailValid,
    setIsEmailValid,
    email,
    setEmail,
    isPolicyChecked,
    setIsPolicyChecked,
    isTelehealthConsent,
    setIsTelehealthConsent,
    isNextButtonEnable,
    uploadPersonalInfo,
    getUserInfo,
    stateOptions,
    isLoading,
  }
}

export const useCareInsurancePay = () => {
  const { i18n } = useI18n()
  const { insuranceCardEntry, setInsuranceCardEntry } = useInsuranceCardEntry()
  const { getInsurancePlans } = useHealthieAPIs()
  const { updateClient } = useUpdateClient()

  const [insurancePlan, setInsurancePlan] = useState(
    insuranceCardEntry.insurancePlan || {}
  )
  const [insurancePlanOptions, setInsurancePlanOptions] = useState([])
  const [coverage, setCoverage] = useState(insuranceCardEntry.coverage)
  const [coverageOptions] = useState([
    { value: 'HMO', label: 'HMO', labelEN: 'HMO' },
    { value: 'PPO', label: 'PPO', labelEN: 'PPO' },
  ])
  const [type, setType] = useState(insuranceCardEntry.type)
  const [typeOptions] = useState([
    { value: 'primary', label: 'Primary', labelEN: 'Primary' },
    { value: 'secondary', label: 'Secondary', labelEN: 'Secondary' },
    { value: 'inactive', label: 'Inactive', labelEN: 'Inactive' },
  ])
  const [relationship, setRelationship] = useState(
    insuranceCardEntry.relationship
  )
  const [relationshipOptions] = useState([
    { value: 'Self', label: 'Self', labelEN: 'Self' },
    { value: 'Child', label: 'Child', labelEN: 'Child' },
    { value: 'Spouse', label: 'Spouse', labelEN: 'Spouse' },
    { value: 'None', label: 'None', labelEN: 'None' },
    { value: 'Other', label: 'Other', labelEN: 'Other' },
  ])
  const [policyNumber, setPolicyNumber] = useState(
    insuranceCardEntry.policyNumber
  )
  const [groupNumber, setGroupNumber] = useState(insuranceCardEntry.groupNumber)
  const [companyPhone, setCompanyPhone] = useState(
    insuranceCardEntry.companyPhone
  )
  const { stateList: stateOptions } = useStateList()
  const [addressInfo, setAddressInfo] = useState(
    insuranceCardEntry.addressInfo || {}
  )
  const [policyHolderInfo, setPolicyHolderInfo] = useState(
    insuranceCardEntry.policyHolderInfo || {}
  )

  const { tags } = useMetadataOptions()

  const { applyHealthieTags, removeHealthieTags } = useApplyTags()

  const isCardInfoNextButtonEnable = useMemo(() => {
    return (
      !_.isEmpty(insurancePlan) &&
      !_.isEmpty(coverage) &&
      !_.isEmpty(type) &&
      !_.isEmpty(relationship) &&
      policyNumber
    )
  }, [insurancePlan, coverage, type, relationship, policyNumber])

  const isAddressNextButtonEnable = useMemo(() => {
    return !_.isEmpty(addressInfo)
  }, [addressInfo])

  const saveLocalData = () => {
    setInsuranceCardEntry({
      insurancePlan,
      coverage,
      type,
      relationship,
      policyNumber,
      groupNumber,
      companyPhone,
      addressInfo,
      policyHolderInfo,
    })
  }

  const updateClientInfo = ({ onSuccess } = {}) => {
    saveLocalData()
    const hasPolicy =
      !_.isEmpty(insurancePlan) &&
      !_.isEmpty(type) &&
      !_.isEmpty(policyNumber) &&
      !_.isEmpty(relationship)
    const policy = hasPolicy
      ? {
          insurance_plan_id: insurancePlan.id,
          priority_type: type.toLowerCase(),
          num: policyNumber,
          group_num: groupNumber,
          policy_phone_number: companyPhone,
          holder_relationship: relationship,
        }
      : {}
    if (relationship !== 'Self') {
      policy.holder_dob = moment(policyHolderInfo.dob, 'MM/DD/YYYY').format(
        'YYYY-MM-DD'
      )
      policy.holder_first = policyHolderInfo.firstName
      policy.holder_gender = policyHolderInfo.gender
      policy.holder_last = policyHolderInfo.lastName
      policy.holder_location = {
        line1: addressInfo.line1,
        line2: addressInfo.line2,
        city: addressInfo.city,
        state: addressInfo.state?.code,
        zip: addressInfo.postcode,
        country: 'US',
      }
    }
    updateClient({
      variables: {
        id: getHealthieUserId(),
        location: {
          line1: addressInfo.line1,
          line2: addressInfo.line2,
          city: addressInfo.city,
          state: addressInfo.state?.code,
          zip: addressInfo.postcode,
          country: 'US',
        },
        policies: hasPolicy ? [policy] : [],
      },
      onSuccess: () => {
        onSuccess?.()
      },
    })

    const _insuranceAddress = addressInfo.formattedAddress
      ? addressInfo.formattedAddress
      : `${addressInfo.line1}, ${addressInfo.city} ${addressInfo.state?.code} ${addressInfo.postcode}`
    setBrazeProfile({
      insurancePlan: insurancePlan.payer_name,
      insuranceCoverage: coverage,
      insuranceType: type,
      insuranceAddress: _insuranceAddress,
      insuranceRelationship: relationship,
    })
    if (relationship !== 'Self') {
      setBrazeProfile({
        insurancePolicyHolderName:
          policyHolderInfo.firstName + ' ' + policyHolderInfo.lastName,
      })
    }
  }

  const getInsurancePlansOptions = async () => {
    try {
      const { error, data } = await getInsurancePlans({
        variables: { order_by: 'PAYER_NAME_ASC' },
      })
      if (data) {
        if (data.insurancePlans?.messages) {
          showErrorMessage(data.insurancePlans?.messages, i18n)
        } else {
          setInsurancePlanOptions(_.uniqBy(data?.insurancePlans || [], 'id'))
        }
      } else if (error) {
        showErrorMessage(error, i18n)
      }
    } catch (error) {
      showErrorMessage(error, i18n)
    }
  }

  return {
    insurancePlan,
    setInsurancePlan,
    insurancePlanOptions,
    getInsurancePlansOptions,
    coverage,
    setCoverage,
    coverageOptions,
    type,
    setType,
    typeOptions,
    relationship,
    setRelationship,
    relationshipOptions,
    policyNumber,
    setPolicyNumber,
    groupNumber,
    setGroupNumber,
    companyPhone,
    setCompanyPhone,
    addressInfo,
    setAddressInfo,
    policyHolderInfo,
    setPolicyHolderInfo,
    stateOptions,
    isCardInfoNextButtonEnable,
    isAddressNextButtonEnable,
    updateClientInfo,
    saveLocalData,
    applyHealthieTags,
    removeHealthieTags,
    tags,
  }
}

export const useUpdateClient = () => {
  const { i18n } = useI18n()
  const { healthieUpdateClient } = useHealthieAPIs()
  const { clearAll } = useClearUserInfo()

  const updateClient = async ({ variables, onSuccess = () => {} } = {}) => {
    try {
      const { error, data } = await getHealthieRequest({
        healthieRequest: healthieUpdateClient,
        queryString: HEALTHIE_UPDATE_CLIENT_STRING,
        variables: variables,
        operationName: 'updateClient',
      })
      if (data) {
        if (data.updateClient?.messages) {
          showErrorMessage(data.updateClient?.messages, i18n)
          checkNeedToRelogin({ data, operationName: 'updateClient', clearAll })
        } else {
          onSuccess()
        }
      } else if (error) {
        showErrorMessage(error, i18n)
      }
    } catch (error) {
      showErrorMessage(error, i18n)
    }
  }

  return { updateClient }
}

export const useApplyTags = () => {
  const applyHealthieTags = async ({ tagId, onSuccess = () => {} } = {}) => {
    try {
      const resp = await updateUserTagPromise({
        type: 'add',
        tagId,
      })
      if (resp.code === 200) {
        onSuccess()
      } else {
        // showErrorMessage(error, i18n)
      }
    } catch (error) {
      // showErrorMessage(error, i18n)
    }
  }

  const removeHealthieTags = async ({ tagId, onSuccess = () => {} } = {}) => {
    try {
      const resp = await updateUserTagPromise({
        type: 'remove',
        tagId,
      })
      if (resp.code === 200) {
        onSuccess()
      } else {
        // showErrorMessage(error, i18n)
      }
    } catch (error) {
      // showErrorMessage(error, i18n)
    }
  }

  return { applyHealthieTags, removeHealthieTags }
}

export const useMetadataOptions = () => {
  const authContext = useContext(AuthContext)

  const { metadataOptions, dispatch } = authContext
  const [helpMostOptions, setHelpMostOptions] = useState(
    metadataOptions?.helpMostOptions || []
  )
  const [diseaseHelpOptions, setDiseaseHelpOptions] = useState(
    metadataOptions?.diseaseHelpOptions || []
  )
  const [hearAboutOptions, setHearAboutOptions] = useState(
    metadataOptions?.hearAboutOptions || []
  )
  const [insuranceProviders, setInsuranceProviders] = useState(
    metadataOptions?.insuranceProviders || []
  )
  const [pastDiagnosisOptions, setPastDiagnosisOptions] = useState(
    metadataOptions?.pastDiagnosisOptions || []
  )
  const [tags, setTags] = useState(metadataOptions?.tags || [])
  const [mcaWlcConditions, setMcaWlcConditions] = useState(
    metadataOptions?.mcaWlcConditions || []
  )
  const [mcaWlcExclusions, setMcaWlcExclusions] = useState(
    metadataOptions?.mcaWlcExclusions || []
  )
  const [mcaMskBodyAreas, setMcaMskBodyAreas] = useState(
    metadataOptions?.mcaMskBodyAreas || []
  )
  const [mcaMskExclusions, setMcaMskExclusions] = useState(
    metadataOptions?.mcaMskExclusions || []
  )

  const getMetadataOptions = useCallback(async () => {
    const _options = await getMetadataOptionsPromise()
    if (!_.isEmpty(_options)) {
      setHelpMostOptions(_options.helpMostOptions || [])
      setDiseaseHelpOptions(_options.diseaseHelpOptions || [])
      setHearAboutOptions(_options.hearAboutOptions || [])
      setInsuranceProviders(_options.insuranceProviders || [])
      setTags(_options.tags || [])
      setPastDiagnosisOptions(_options.pastDiagnosisOptions || [])
      setMcaWlcConditions(_options.mcaWlcConditions || [])
      setMcaWlcExclusions(_options.mcaWlcExclusions || [])
      setMcaMskBodyAreas(_options.mcaMskBodyAreas || [])
      setMcaMskExclusions(_options.mcaMskExclusions || [])
      dispatch({
        type: 'METADATA_OPTIONS',
        payload: {
          metadataOptions: {
            ..._options,
          },
        },
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    getMetadataOptions()
  }, [getMetadataOptions])

  return {
    helpMostOptions,
    diseaseHelpOptions,
    hearAboutOptions,
    insuranceProviders,
    tags,
    pastDiagnosisOptions,
    mcaWlcConditions,
    mcaWlcExclusions,
    mcaMskBodyAreas,
    mcaMskExclusions,
  }
}

export const useStep4Location = ({ sku } = {}) => {
  const { personalInfo } = usePersonalInfo()
  const { updateQuickNotes } = useUpdateQuickNotes()
  const { setUnavailableStatus } = useUnavailableStatus()
  const { getUserProfileStatus } = useUserProfileStatus()

  const getNextScreen = async ({ survey, hormonalAutoimmune }) => {
    const state = personalInfo.state || {}

    const quickNotes = hormonalAutoimmune
      ? `${survey.name}: ${
          hormonalAutoimmune.otherText
            ? `${hormonalAutoimmune.name} : ${hormonalAutoimmune.otherText}`
            : hormonalAutoimmune.name
        }`
      : `${
          survey.otherText
            ? `${survey.name} : ${survey.otherText}`
            : survey.name
        }`
    updateQuickNotes(quickNotes)

    if (!state.available) {
      setUnavailableStatus(UnavailableStatus.COMING_SOON_STATE)
      return ScreenKey.UNAVAILABLE
    } else if (!survey.available) {
      setUnavailableStatus(UnavailableStatus.LIMITED_AVAILABILITY_SURVEY)
      updateUserPromise({
        skuStatus: {
          sku,
          status: 5,
        },
      })
      return ScreenKey.UNAVAILABLE
    } else if (hormonalAutoimmune && !hormonalAutoimmune.available) {
      setUnavailableStatus(
        UnavailableStatus.LIMITED_AVAILABILITY_HORMONAL_AUTOIMMUNE
      )
      updateUserPromise({
        skuStatus: {
          sku,
          status: 5,
        },
      })
      return ScreenKey.UNAVAILABLE
    } else {
      const { shouldSkipFlow } = await getUserProfileStatus()
      return shouldSkipFlow
        ? ScreenKey.SCHEDULED_TIME
        : ScreenKey.COMPLETE_PROFILE
    }
  }

  const isOptionsValid = ({ state, helpMostOption, diseaseHelpOption }) => {
    if (!state.available) {
      return false
    } else if (
      !helpMostOption.available ||
      (diseaseHelpOption && !diseaseHelpOption.available)
    ) {
      return false
    } else {
      return true
    }
  }

  return { getNextScreen, isOptionsValid }
}

export const usePrimaryPhysician = () => {
  const authContext = useContext(AuthContext)

  const { primaryPhysician, dispatch } = authContext

  const { i18n } = useI18n()
  const { createReferral } = useHealthieAPIs()

  const createReferring = async ({
    variables,
    onSuccess = (id) => {},
  } = {}) => {
    try {
      const body = _.omitBy(variables, _.isEmpty)
      body.acceptsInsurance = true
      const data = await createReferringPhysicianPromise(body)
      if (data) {
        if (data.code !== 200) {
          showErrorMessage({ message: data.msg }, i18n)
        } else {
          onSuccess(data.data?.referringPhysician?.id)
        }
      }
    } catch (error) {
      showErrorMessage(error, i18n)
    }
  }

  const createReferralFun = async ({
    variables,
    onSuccess = (id) => {},
  } = {}) => {
    try {
      const { error, data } = await createReferral({
        variables: variables,
      })
      if (data) {
        if (data.createReferral?.messages) {
          showErrorMessage(data.createReferral?.messages, i18n)
        } else {
          onSuccess()
        }
      } else if (error) {
        showErrorMessage(error, i18n)
      }
    } catch (error) {
      showErrorMessage(error, i18n)
    }
  }

  const saveLocalPrimaryPhysician = (physician) => {
    dispatch({
      type: 'PRIMARY_PHYSICIAN',
      payload: {
        primaryPhysician: physician,
      },
    })
  }

  return {
    saveLocalPrimaryPhysician,
    primaryPhysician: primaryPhysician || [],
    createReferring,
    createReferralFun,
  }
}

export const useCardPay = ({
  setLoading,
  stripe,
  elements,
  product,
  onPurchaseFailure,
  onPurchaseSuccess,
}) => {
  const { i18n } = useI18n()
  const [isComplete, setIsComplete] = useState(false)

  const cardStyle = {
    style: {
      base: {
        color: '#323555',
        fontFamily: 'Arial, sans-serif',
        fontSmoothing: 'antialiased',
        fontSize: '16px',
        '::placeholder': {
          color: '#757575',
        },
      },
      invalid: {
        color: '#fa755a',
        iconColor: '#fa755a',
      },
    },
  }
  const handleCardChange = async (event) => {
    // Listen for changes in the CardElement
    // and display any errors as the customer types their card details
    setIsComplete(event.complete)
  }

  const handleCardSubmitWithoutProduct = async (event) => {
    setLoading(true)
    event.stopPropagation()
    event.preventDefault()
    if (!stripe || !elements) {
      // Stripe.js has not loaded yet. Make sure to disable
      // form submission until Stripe.js has loaded.
      showNotification({
        type: 'error',
        title: i18n('There is something wrong, please try again later.'),
      })
      setLoading(false)
      return
    }

    const onError = ({ message, code, status } = {}) => {
      showNotification({
        type: 'error',
        title: message || i18n('Save failed!'),
      })
      if (onPurchaseFailure) return onPurchaseFailure({ message, code, status })
    }

    let _paymentMethodId
    const cardElement = elements.getElement(CardElement)
    const { error, paymentMethod } = await stripe.createPaymentMethod({
      type: 'card',
      card: cardElement,
    })
    if (error) {
      onError({
        ...error,
        status: error.type,
      })
      setLoading(false)
      return
    } else {
      _paymentMethodId = paymentMethod?.id
    }

    const onSuccess = () => {
      if (onPurchaseSuccess) return onPurchaseSuccess()
    }

    const payResp = await addStripeInfoPromise({
      paymentMethodId: _paymentMethodId,
    })
    if (payResp?.code === 200) {
      onSuccess()
    } else {
      onError({ message: payResp?.msg })
    }

    setLoading(false)
  }

  const handleCardSubmit = async (event) => {
    setLoading(true)
    event.stopPropagation()
    event.preventDefault()
    if (!stripe || !elements) {
      // Stripe.js has not loaded yet. Make sure to disable
      // form submission until Stripe.js has loaded.
      showNotification({
        type: 'error',
        title: i18n('There is something wrong, please try again later.'),
      })
      setLoading(false)
      return
    }

    const onError = ({ message, code, status } = {}) => {
      showNotification({
        type: 'error',
        title: message || i18n('Save failed!'),
      })
      if (onPurchaseFailure) return onPurchaseFailure({ message, code, status })
    }

    const cardElement = elements.getElement(CardElement)
    const { error, paymentMethod } = await stripe.createPaymentMethod({
      type: 'card',
      card: cardElement,
    })
    if (error) {
      onError({
        ...error,
        status: error.type,
      })
      setLoading(false)
      return
    }

    if (onPurchaseSuccess) return onPurchaseSuccess(paymentMethod)
    setLoading(false)
  }

  const onCardPay = async (paymentMethod) => {
    const onSuccess = () => {
      if (onPurchaseSuccess) return onPurchaseSuccess()
      // showNotification({ type: 'success', title: i18n('Payment success!') })
    }

    const onError = ({ message, code, status } = {}) => {
      showNotification({
        type: 'error',
        title: message || i18n('Payment failed!'),
      })
      if (onPurchaseFailure) return onPurchaseFailure({ message, code, status })
    }

    const payResp = await payStripePromise({
      userId: getUserId(),
      paymentMethodId: paymentMethod?.id,
      productId: product.id,
    })
    if (payResp && !_.isEmpty(payResp.data)) {
      if (
        payResp?.data?.requiresConfirmation === true ||
        payResp?.data?.requiresAction === true
      ) {
        let promise
        promise = stripe.confirmCardPayment(
          payResp?.data?.paymentIntentClientSecret
        )
        await promise.then(async (result) => {
          if (result?.paymentIntent?.status === 'succeeded') {
            onSuccess()
          } else {
            onError({ message: payResp.msg })
          }
        })
      } else {
        onSuccess()
      }
    } else {
      onError({ message: payResp?.msg })
    }
  }

  return {
    cardStyle,
    handleCardChange,
    handleCardSubmit,
    handleCardSubmitWithoutProduct,
    onCardPay,
    isComplete,
  }
}

export const useHealthieUserInfo = () => {
  const [user, setUser] = useState({})
  const { getUser } = useHealthieAPIs()

  const getHealthieUser = useCallback(async () => {
    const userId = getHealthieUserId()

    if (userId) {
      const { data } = await getHealthieRequest({
        healthieRequest: getUser,
        queryString: HEALTHIE_GET_USER_STRING,
        variables: { id: userId, include_tags: true },
        operationName: 'getUser',
      })
      if (data && !_.isEmpty(data.user)) {
        setUser(data.user)
      }
    }
  }, [getUser])

  useEffect(() => {
    getHealthieUser()
  }, [getHealthieUser])

  return { user }
}

export const getHealthieRequest = async ({
  healthieRequest,
  queryString,
  variables,
  operationName,
}) => {
  const fitonToken = getToken()

  let data = {}
  let error = {}
  if (fitonToken) {
    data = await forwardHealthiePromise({
      query: queryString,
      operationName,
      variables,
    })
  } else {
    return {}
  }
  return { error, data }
}

export const checkNeedToRelogin = ({ data, operationName, clearAll }) => {
  if (!_.isEmpty(data?.[operationName]?.messages)) {
    const message = data?.[operationName]?.messages[0]
    if (
      message?.message === 'You need to sign in or sign up before continuing'
    ) {
      clearAll()
      return true
    }
  }
  return false
}
